import { redirect } from "react-router";
import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import { APP_ID } from "@/constants/appId";
import { IRoleEnum } from "@interfaces";
import { routePath } from "@routes/path.ts";
import { useUiStore } from "@stores/uiStore";
import {
  IAuthStepEnum,
  useAuthStore,
} from "@templates/authManagement/authStore";

import type { ID } from "@interfaces";
import type { IUserStore } from "@stores/userStore/types";

const name = `${APP_ID}/USER_STORE`;

export const useUserStore = create<IUserStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set) => ({
          id: "",
          storeId: "",
          centralWarehouseId: "",
          scrapsWarehouseId: "",
          isAuthenticated: false,
          login: (
            token: string,
            role: IRoleEnum | null,
            id: ID,
            storeId: ID | null,
            centralWarehouseId: ID | null,
            scrapsWarehouseId: ID | null,
          ) => {
            set({
              isAuthenticated: true,
              token,
              role,
              id,
              storeId,
              centralWarehouseId,
              scrapsWarehouseId,
            });
          },
          logout: () => {
            set({ isAuthenticated: false, token: null, role: null });
            useUiStore.setState({ pageMode: "index" });
            useAuthStore.getState().resetState();
            redirect(
              routePath.authManagement.auth({
                type: IAuthStepEnum.LOGIN?.toString(),
              }),
            );
          },
          role: IRoleEnum.SUPER_ADMIN,
          token: null,
        }),
        {
          name,
          partialize: (state) => ({
            role: state.role,
            token: state.token,
            id: state.id,
            storeId: state.storeId,
            centralWarehouseId: state.centralWarehouseId,
            scrapsWarehouseId: state.scrapsWarehouseId,
          }),
        },
      ),
      {
        enabled: !import.meta.env.PROD,
        name,
      },
    ),
  ),
);

export const useRole = () => useUserStore(useShallow((state) => state.role));
export const useStoreId = () =>
  useUserStore(useShallow((state) => state.storeId));
export const useWarehouseId = () =>
  useUserStore(useShallow((state) => state.centralWarehouseId));

const initialState = useUserStore.getState();

if (initialState.token) {
  useUserStore.setState({ isAuthenticated: true });
}
